import { useState, useEffect } from 'react';
import { 
  Card, CardContent, Typography, Box, Button, Dialog, DialogTitle, 
  DialogContent, DialogActions, TextField, Select, MenuItem, FormControl, 
  InputLabel, LinearProgress, Chip, IconButton, Grid, Alert
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import FlagIcon from '@mui/icons-material/Flag';
import GoalProgressUpdate from './GoalProgressUpdate';
import { api, authenticatedFetch } from '../utils/api';
import { format, differenceInDays } from 'date-fns';

function GoalSystem() {
  const [goals, setGoals] = useState([]);
  const [open, setOpen] = useState(false);
  const [editingGoal, setEditingGoal] = useState(null);
  const [progressUpdateOpen, setProgressUpdateOpen] = useState(false);
  const [selectedGoal, setSelectedGoal] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    type: 'weight',
    target: '',
    current: '',
    deadline: '',
    priority: 'medium',
    status: 'active'
  });

  useEffect(() => {
    fetchGoals();
  }, []);

  const fetchGoals = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(api.goals, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      setGoals(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error fetching goals:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem('token');
    
    const goalData = {
      ...formData,
      target: Number(formData.target),
      current: Number(formData.current)
    };

    try {
      const url = editingGoal 
        ? api.goals + `/${editingGoal._id}`
        : api.goals;
      
      const response = await fetch(url, {
        method: editingGoal ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(goalData)
      });

      if (response.ok) {
        fetchGoals();
        handleClose();
      }
    } catch (error) {
      console.error('Error saving goal:', error);
    }
  };

  const handleEdit = (goal) => {
    setEditingGoal(goal);
    setFormData({
      title: goal.title,
      type: goal.type,
      target: goal.target.toString(),
      current: goal.current.toString(),
      deadline: goal.deadline ? format(new Date(goal.deadline), 'yyyy-MM-dd') : '',
      priority: goal.priority,
      status: goal.status || 'active'
    });
    setOpen(true);
  };

  const handleDelete = async (goalId) => {
    if (window.confirm('Are you sure you want to delete this goal?')) {
      try {
        const token = localStorage.getItem('token');
        await fetch(`${api.goals}/${goalId}`, {
          method: 'DELETE',
          headers: { 'Authorization': `Bearer ${token}` }
        });
        fetchGoals();
      } catch (error) {
        console.error('Error deleting goal:', error);
      }
    }
  };

  const handleProgressUpdate = (goal) => {
    setSelectedGoal(goal);
    setProgressUpdateOpen(true);
  };

  const handleProgressUpdateComplete = (updatedGoal) => {
    setGoals(goals.map(goal => goal._id === updatedGoal._id ? updatedGoal : goal));
  };

  const handleClose = () => {
    setOpen(false);
    setEditingGoal(null);
    setFormData({
      title: '',
      type: 'weight',
      target: '',
      current: '',
      deadline: '',
      priority: 'medium',
      status: 'active'
    });
  };

  const getProgress = (current, target, type) => {
    if (type === 'weight') {
      // For weight loss, progress = (start - current) / (start - target)
      // Assuming start weight is higher than target
      return Math.min(100, Math.max(0, (current / target) * 100));
    }
    return Math.min(100, Math.max(0, (current / target) * 100));
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return 'error';
      case 'medium': return 'warning';
      case 'low': return 'info';
      default: return 'default';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'success';
      case 'paused': return 'warning';
      case 'completed': return 'info';
      default: return 'default';
    }
  };

  const getTypeIcon = (type) => {
    const icons = {
      weight: '⚖️',
      energy: '⚡',
      sleep: '😴',
      exercise: '💪',
      mood: '😊',
      habit: '🎯'
    };
    return icons[type] || '🎯';
  };

  const getDaysRemaining = (deadline) => {
    if (!deadline) return null;
    const days = differenceInDays(new Date(deadline), new Date());
    return days;
  };

  const goalTypes = [
    { value: 'weight', label: 'Weight Goal' },
    { value: 'energy', label: 'Energy Level' },
    { value: 'sleep', label: 'Sleep Hours' },
    { value: 'exercise', label: 'Exercise Goal' },
    { value: 'mood', label: 'Mood Improvement' },
    { value: 'habit', label: 'Daily Habit' }
  ];

  return (
    <Box sx={{ p: 3, maxWidth: 1000, mx: 'auto' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 700, color: 'primary.main' }}>
          🎯 My Goals
        </Typography>
        <Button 
          variant="contained" 
          startIcon={<AddIcon />} 
          onClick={() => setOpen(true)}
          sx={{ borderRadius: 3 }}
        >
          New Goal
        </Button>
      </Box>

      {goals.length === 0 ? (
        <Card sx={{ p: 4, textAlign: 'center' }}>
          <FlagIcon sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No goals set yet
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Set your first health goal to start tracking your progress!
          </Typography>
          <Button variant="contained" onClick={() => setOpen(true)}>
            Create Your First Goal
          </Button>
        </Card>
      ) : (
        <Grid container spacing={3}>
          {goals.map((goal) => {
            const progress = getProgress(goal.current, goal.target, goal.type);
            const daysRemaining = getDaysRemaining(goal.deadline);
            
            return (
              <Grid item xs={12} md={6} lg={4} key={goal._id}>
                <Card sx={{ height: '100%', position: 'relative' }}>
                  <CardContent sx={{ pb: 1 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                          {getTypeIcon(goal.type)} {goal.title}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                          <Chip 
                            label={goal.priority.toUpperCase()} 
                            size="small" 
                            color={getPriorityColor(goal.priority)}
                          />
                          <Chip 
                            label={goal.status.toUpperCase()} 
                            size="small" 
                            color={getStatusColor(goal.status)}
                          />
                        </Box>
                      </Box>
                      <Box>
                        <IconButton size="small" onClick={() => handleProgressUpdate(goal)} title="Update Progress">
                          <TrendingUpIcon fontSize="small" />
                        </IconButton>
                        <IconButton size="small" onClick={() => handleEdit(goal)} title="Edit Goal">
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton size="small" onClick={() => handleDelete(goal._id)} title="Delete Goal">
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </Box>

                    <Box sx={{ mb: 2 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="body2">Progress</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {goal.current} / {goal.target}
                        </Typography>
                      </Box>
                      <LinearProgress 
                        variant="determinate" 
                        value={progress} 
                        sx={{ height: 8, borderRadius: 4 }}
                        color={progress >= 80 ? 'success' : progress >= 50 ? 'primary' : 'warning'}
                      />
                      <Typography variant="caption" color="text.secondary">
                        {Math.round(progress)}% complete
                      </Typography>
                    </Box>

                    {daysRemaining !== null && (
                      <Alert 
                        severity={daysRemaining < 7 ? 'warning' : daysRemaining < 30 ? 'info' : 'success'} 
                        sx={{ mt: 2 }}
                      >
                        {daysRemaining > 0 
                          ? `${daysRemaining} days remaining` 
                          : daysRemaining === 0 
                            ? 'Due today!' 
                            : `${Math.abs(daysRemaining)} days overdue`
                        }
                      </Alert>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      )}

      {/* Goal Creation/Edit Dialog */}
      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingGoal ? 'Edit Goal' : 'Create New Goal'}
        </DialogTitle>
        <DialogContent>
          <Box component="form" onSubmit={handleSubmit} sx={{ pt: 1 }}>
            <TextField
              fullWidth
              label="Goal Title"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              sx={{ mb: 2 }}
              required
            />
            
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Goal Type</InputLabel>
              <Select
                value={formData.type}
                label="Goal Type"
                onChange={(e) => setFormData({ ...formData, type: e.target.value })}
              >
                {goalTypes.map((type) => (
                  <MenuItem key={type.value} value={type.value}>
                    {getTypeIcon(type.value)} {type.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Current Value"
                  type="number"
                  value={formData.current}
                  onChange={(e) => setFormData({ ...formData, current: e.target.value })}
                  required
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Target Value"
                  type="number"
                  value={formData.target}
                  onChange={(e) => setFormData({ ...formData, target: e.target.value })}
                  required
                />
              </Grid>
            </Grid>

            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={4}>
                <TextField
                  fullWidth
                  label="Deadline"
                  type="date"
                  value={formData.deadline}
                  onChange={(e) => setFormData({ ...formData, deadline: e.target.value })}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={4}>
                <FormControl fullWidth>
                  <InputLabel>Priority</InputLabel>
                  <Select
                    value={formData.priority}
                    label="Priority"
                    onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
                  >
                    <MenuItem value="low">Low</MenuItem>
                    <MenuItem value="medium">Medium</MenuItem>
                    <MenuItem value="high">High</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={4}>
                <FormControl fullWidth>
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={formData.status}
                    label="Status"
                    onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                  >
                    <MenuItem value="active">Active</MenuItem>
                    <MenuItem value="paused">Paused</MenuItem>
                    <MenuItem value="completed">Completed</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained">
            {editingGoal ? 'Update Goal' : 'Create Goal'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Progress Update Dialog */}
      <GoalProgressUpdate
        open={progressUpdateOpen}
        onClose={() => setProgressUpdateOpen(false)}
        goal={selectedGoal}
        onUpdate={handleProgressUpdateComplete}
      />
    </Box>
  );
}

export default GoalSystem;
